// src/pages/TechnologicalCardDirectory.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate"; // <-- Добавляем импорт
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import {
  getTechnologicalCards,
  createTechnologicalCard,
  updateTechnologicalCard,
  deleteTechnologicalCard,
  getNomenclatures,
} from "../api/enterprise";
import { notifyError } from "../utils/toasts";

function TechnologicalCardDirectory({ divisionId, enterpriseId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [technologicalCards, setTechnologicalCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Для пагинации
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const [newCardName, setNewCardName] = useState("");
  const [nomenclatures, setNomenclatures] = useState([]);
  const [selectedNomenclatureId, setSelectedNomenclatureId] = useState("");

  // Состояния для редактирования
  const [isEditing, setIsEditing] = useState(false);
  const [editingCardId, setEditingCardId] = useState(null);
  const [editCardName, setEditCardName] = useState("");
  const [editSelectedNomenclatureId, setEditSelectedNomenclatureId] =
    useState("");
  const [editHasOperations, setEditHasOperations] = useState(false);

  // Модальное окно подтверждения удаления
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);

  // Получаем данные
  useEffect(() => {
    const fetchTechnologicalCards = async () => {
      if (divisionId) {
        try {
          const result = await getTechnologicalCards(divisionId);
          setTechnologicalCards(result);
        } catch (error) {
          notifyError(error?.response?.data?.message || error.message);
        }
      }
    };
    fetchTechnologicalCards();
  }, [divisionId]);

  useEffect(() => {
    const fetchNomenclatures = async () => {
      try {
        const result = await getNomenclatures(enterpriseId);
        setNomenclatures(result);
      } catch (error) {
        notifyError(error?.response?.data?.message || error.message);
      }
    };
    fetchNomenclatures();
  }, [enterpriseId]);

  // Фильтрация
  useEffect(() => {
    const lowercasedQuery = searchQuery.toLowerCase();
    const filtered = technologicalCards.filter((item) =>
      Object.values(item).some(
        (value) =>
          value && value.toString().toLowerCase().includes(lowercasedQuery)
      )
    );
    setFilteredData(filtered);
    // При изменении поискового запроса сбрасываем страницу на 0,
    // чтобы пользователь видел результаты с первой страницы.
    setCurrentPage(0);
  }, [searchQuery, technologicalCards]);

  // Пагинация: вычисляем срез данных
  const offset = currentPage * itemsPerPage;
  const currentItems = filteredData.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // Добавление карты
  const handleAddTechnologicalCard = async () => {
    if (newCardName.trim() && selectedNomenclatureId) {
      try {
        const response = await createTechnologicalCard(
          divisionId,
          newCardName,
          selectedNomenclatureId
        );
        const newCardId = response.id;

        setNewCardName("");
        setSelectedNomenclatureId("");

        // Переходим на страницу новой технологической карты
        navigate(`/${enterpriseId}/product-sheet/${newCardId}`);
      } catch (error) {
        notifyError(error?.response?.data?.message || error.message);
      }
    }
  };

  // Редактирование
  const handleEditTechnologicalCard = (item) => {
    setIsEditing(true);
    setEditingCardId(item.product_sheet_id);
    setEditCardName(item.name);
    setEditSelectedNomenclatureId(item.final_nomenclature_id || "");
    setEditHasOperations(item.has_operations);
  };

  const handleSaveEditedCard = async () => {
    if (editCardName.trim()) {
      try {
        await updateTechnologicalCard(
          editingCardId,
          editCardName,
          editSelectedNomenclatureId
        );
        const result = await getTechnologicalCards(divisionId);
        setTechnologicalCards(result);

        // Сброс состояния редактирования
        setIsEditing(false);
        setEditingCardId(null);
        setEditCardName("");
        setEditSelectedNomenclatureId("");
        setEditHasOperations(false);
      } catch (error) {
        notifyError(error?.response?.data?.message || error.message);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCardId(null);
    setEditCardName("");
    setEditSelectedNomenclatureId("");
    setEditHasOperations(false);
  };

  // Удаление
  const handleDeleteClick = (item, e) => {
    e.stopPropagation(); // чтобы не срабатывало редактирование
    setCardToDelete(item);
    setShowConfirmModal(true);
  };

  // Шаг 2: при подтверждении в модалке реально удаляем
  const handleConfirmDelete = async () => {
    if (!cardToDelete) return;
    try {
      await deleteTechnologicalCard(cardToDelete.product_sheet_id);
      const result = await getTechnologicalCards(divisionId);
      setTechnologicalCards(result);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    } finally {
      setShowConfirmModal(false);
      setCardToDelete(null);
    }
  };

  // Определяем колонки
  const columns = [
    { header: t("name"), accessor: "name" },
    { header: t("final_nomenclature"), accessor: "final_nomenclature_name" },
    { header: t("upserted_at"), accessor: "upserted_at" },
  ];

  return (
    <div className="container">
      <h1>{t("technological_cards")}</h1>
      <div className="card">
        <div className="card-header">
          <input
            type="text"
            placeholder={t("search_by_param")}
            className="form-control"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "200px",
              display: "inline-block",
              marginRight: "20px",
            }}
          />
        </div>

        <div className="card-body p-0">
          <table className="table table-bordered table-striped table-hover projects">
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                {columns.map((column, idx) => (
                  <th key={idx} style={{ width: "auto" }}>
                    {column.header}
                  </th>
                ))}
                <th style={{ width: "20%", minWidth: "168px" }}>
                  {t("actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((row, idx) => {
                // Используем offset, чтобы корректно выводить общий индекс
                const rowIndex = offset + idx + 1;
                return (
                  <tr
                    key={idx}
                    onClick={() => handleEditTechnologicalCard(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{rowIndex}</td>
                    {columns.map((column, colIdx) => (
                      <td key={colIdx}>
                        {column.accessor === "final_nomenclature_name"
                          ? row[column.accessor] || "—"
                          : column.accessor === "upserted_at"
                          ? new Date(row[column.accessor]).toLocaleString()
                          : row[column.accessor]}
                      </td>
                    ))}
                    <td className="project-actions text-right">
                      <button
                        className="btn btn-info btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/${enterpriseId}/product-sheet/${row.product_sheet_id}`
                          );
                        }}
                      >
                        <i className="fas fa-folder mr-1"></i>
                        {t("view")}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={(e) => handleDeleteClick(row, e)}
                      >
                        <i className="fas fa-trash mr-1"></i>
                        {t("delete")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Добавляем футер с пагинацией */}
        <div className="card-footer">
          <div className="d-flex justify-content-end">
            <ReactPaginate
              previousLabel={t("prev")}
              nextLabel={t("next")}
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName="pagination"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
              forcePage={currentPage} // при поиске сбрасываемся на страницу 0
            />
          </div>
        </div>
      </div>

      {/* Блок для добавления новой технологической карты или редактирования */}
      <div className="card mt-4">
        <div className="card-body">
          {isEditing ? (
            <>
              <h5>{t("edit_technological_card")}</h5>
              <div
                className="input-group mb-3"
                style={{ justifyContent: "space-between" }}
              >
                <div style={{ display: "flex" }}>
                  <div className="form-group" style={{ margin: "0 5px 0 0" }}>
                    <label htmlFor="name">{t("name")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("enter_name")}
                      value={editCardName}
                      onChange={(e) => setEditCardName(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ margin: "0 5px 0 0" }}>
                    <label htmlFor="nomenclature">
                      {t("nomenclature")}
                    </label>
                    <select
                      className="form-control"
                      value={editSelectedNomenclatureId}
                      onChange={(e) =>
                        setEditSelectedNomenclatureId(e.target.value)
                      }
                      disabled={editHasOperations}
                    >
                      <option value="">{t("select_nomenclature")}</option>
                      {nomenclatures.map((nomenclature) => (
                        <option
                          key={nomenclature.nomenclature_id}
                          value={nomenclature.nomenclature_id}
                        >
                          {nomenclature.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex" }}>
                  <div
                    className="input-group-append"
                    style={{
                      height: "37px",
                      alignSelf: "flex-end",
                      marginBottom: "8px",
                    }}
                  >
                    <button
                      style={{
                        borderTopLeftRadius: "6px",
                        borderBottomLeftRadius: "6px",
                        marginRight: "5px",
                      }}
                      className="btn btn-success"
                      onClick={handleSaveEditedCard}
                      disabled={!editCardName.trim()}
                    >
                      {t("save")}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleCancelEdit}
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              </div>
              {editHasOperations && (
                <small className="text-muted">
                  {t("cannot_change_nomenclature")}
                </small>
              )}
            </>
          ) : (
            <>
              <h5>{t("add_new_technological_card")}</h5>
              <div
                className="input-group mb-3"
                style={{ justifyContent: "space-between" }}
              >
                <div style={{ display: "flex" }}>
                  <div className="form-group" style={{ margin: "0 5px 0 0" }}>
                    <label htmlFor="name">{t("name")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("enter_name")}
                      value={newCardName}
                      onChange={(e) => setNewCardName(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ margin: "0 5px 0 0" }}>
                    <label htmlFor="nomenclature">
                      {t("nomenclature")}
                    </label>
                    <select
                      className="form-control"
                      value={selectedNomenclatureId}
                      onChange={(e) =>
                        setSelectedNomenclatureId(e.target.value)
                      }
                    >
                      <option value="">{t("select_nomenclature")}</option>
                      {nomenclatures.map((nomenclature) => (
                        <option
                          key={nomenclature.nomenclature_id}
                          value={nomenclature.nomenclature_id}
                        >
                          {nomenclature.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div
                  className="input-group-append"
                  style={{
                    height: "37px",
                    alignSelf: "flex-end",
                    marginBottom: "8px",
                  }}
                >
                  <button
                    style={{
                      borderTopLeftRadius: "6px",
                      borderBottomLeftRadius: "6px",
                    }}
                    className="btn btn-success"
                    onClick={handleAddTechnologicalCard}
                    disabled={!newCardName.trim() || !selectedNomenclatureId}
                  >
                    {t("add_new")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Модальное окно подтверждения удаления */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_delete_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_message")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TechnologicalCardDirectory;
